import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Collapse, Descriptions, Form, Input, InputNumber, Modal, Radio, Select, Space,
    Spin, Table, Tag, Typography, message,
} from 'antd';
import { ArrowLeftOutlined, CheckOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import client from '../api/client';

const { Text } = Typography;

const BASE = '/transactions/rice-allowances/monthly-upload';
const statusColor = { DRAFT: 'default', APPROVED: 'processing', RELEASED: 'success', CANCELLED: 'error' };
const money = (value) => `₱${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const apiError = (error, fallback) => {
    const data = error.response?.data;
    const first = data?.errors ? Object.values(data.errors).flat()[0] : null;
    return first || data?.message || fallback;
};

/**
 * Review of a Monthly Upload draft: unresolved Excel rows (correct or dismiss them)
 * and the recipients already in the draft, each with its imported Batch label.
 * Approval and release stay with the existing Rice Allowance actions.
 */
const RiceAllowanceUploadReview = ({ batchId, items = [], onBack, onChanged }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [correcting, setCorrecting] = useState(null);
    const [dismissing, setDismissing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [picked, setPicked] = useState({});
    const [confirmingRow, setConfirmingRow] = useState(null);
    const [correctForm] = Form.useForm();
    const [dismissForm] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: review } = await client.get(`${BASE}/${batchId}`);
            setData(review);
            const employeeRes = await client.get('/transactions/rice-allowances/employees', { params: { branch_id: review.batch.branch?.id } });
            setEmployees(employeeRes.data || []);
        } catch (error) {
            message.error(apiError(error, 'Failed to load the upload draft'));
        } finally {
            setLoading(false);
        }
    }, [batchId]);

    useEffect(() => { load(); }, [load]);

    const unresolved = useMemo(() => (data?.rows || []).filter((row) => row.status === 'UNRESOLVED'), [data]);
    const history = useMemo(() => (data?.rows || []).filter((row) => ['CORRECTED', 'DISMISSED'].includes(row.status)), [data]);
    const isDraft = data?.batch.status === 'DRAFT';

    const employeeOptions = employees.map((employee) => ({ value: employee.id, label: `${employee.name} [${employee.code}]` }));
    const itemOptions = items.map((item) => ({ value: item.id, label: `${item.name}${item.code ? ` [${item.code}]` : ''}` }));

    const openCorrect = (row) => {
        // Employee suggestions come from EMPLOYEE_* issues only (a word-order match to confirm, or the people sharing a name).
        const employeeCandidates = row.issues
            .filter((issue) => issue.code.startsWith('EMPLOYEE_'))
            .flatMap((issue) => issue.candidates || [])
            .filter((candidate) => employees.some((employee) => employee.id === candidate.id));
        const suggestedEmployee = row.employee_id || (employeeCandidates.length === 1 ? employeeCandidates[0].id : undefined);
        setCorrecting({ row, suggestions: employeeCandidates });
        correctForm.setFieldsValue({
            employee_id: suggestedEmployee,
            item_id: row.item_id || undefined,
            allowance_kg: row.kg ?? undefined,
            unit_price: row.price ?? undefined,
            batch_label: row.batch_label || '',
        });
    };

    const submitCorrection = async (values) => {
        setSaving(true);
        try {
            const { data: review } = await client.post(`${BASE}/${batchId}/rows/${correcting.row.id}/resolve`, {
                ...values,
                batch_label: values.batch_label?.trim() || null,
            });
            setData(review);
            setCorrecting(null);
            message.success('Row added to the draft');
            onChanged?.();
        } catch (error) {
            message.error(apiError(error, 'Failed to correct the row'));
        } finally {
            setSaving(false);
        }
    };

    const confirmEmployee = async (row) => {
        setConfirmingRow(row.id);
        try {
            const { data: review } = await client.post(`${BASE}/${batchId}/rows/${row.id}/confirm-employee`, { employee_id: picked[row.id] });
            setData(review);
            message.success('Employee confirmed');
            onChanged?.();
        } catch (error) {
            message.error(apiError(error, 'Failed to confirm the employee'));
        } finally {
            setConfirmingRow(null);
        }
    };

    const submitDismissal = async (values) => {
        setSaving(true);
        try {
            const { data: review } = await client.post(`${BASE}/${batchId}/rows/${dismissing.id}/dismiss`, { reason: values.reason.trim() });
            setData(review);
            setDismissing(null);
            dismissForm.resetFields();
            message.success('Row dismissed');
            onChanged?.();
        } catch (error) {
            message.error(apiError(error, 'Failed to dismiss the row'));
        } finally {
            setSaving(false);
        }
    };

    if (loading && !data) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
    if (!data) return <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Back to drafts</Button>;

    const { batch, summary } = data;

    const unresolvedColumns = [
        { title: 'Row', dataIndex: 'row_number', width: 50 },
        { title: 'Batch', dataIndex: 'batch_label', width: 80, render: (value) => value || <Text type="secondary">—</Text> },
        { title: 'Employee Name', dataIndex: 'employee_name', width: 140 },
        { title: 'Batch KG', dataIndex: 'kg_input', width: 70, align: 'right' },
        { title: 'Rice Item', dataIndex: 'rice_item_input', width: 120 },
        { title: 'Price', dataIndex: 'price_input', width: 70, align: 'right' },
        {
            title: 'What needs correcting', width: 400,
            render: (_, row) => {
                const errors = row.issues.filter((issue) => issue.level === 'error');
                const confirmed = row.issues.some((issue) => issue.code === 'EMPLOYEE_CONFIRMED');
                return (
                    <Space direction="vertical" size={4}>
                        {row.employee && (
                            <Text type="success">{confirmed ? 'Confirmed employee' : 'Matched employee'}: <Text strong type="success">{row.employee.name} [{row.employee.code}]</Text></Text>
                        )}
                        {row.employee && errors.length > 0 && <Text strong>Still needs correction:</Text>}
                        {errors.map((issue) => <Text key={issue.code + issue.message} type="danger">{issue.message}</Text>)}
                    </Space>
                );
            },
        },
        {
            title: 'Actions', width: 190, fixed: 'right',
            render: (_, row) => isDraft && <Space>
                <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => openCorrect(row)}>Correct</Button>
                <Button size="small" icon={<StopOutlined />} onClick={() => { dismissForm.resetFields(); setDismissing(row); }}>Dismiss</Button>
            </Space>,
        },
    ];

    const recipientColumns = [
        {
            title: 'Employee', width: 280,
            render: (_, row) => <span>
                <Text strong>{row.employee?.name}</Text> <Text type="secondary">[{row.employee?.code}]</Text>
                {row.batch_label && <Tag color="blue" style={{ marginLeft: 8 }}>{row.batch_label}</Tag>}
            </span>,
        },
        { title: 'Rice Item', render: (_, row) => row.item?.name, width: 170 },
        { title: 'Default KG', dataIndex: 'original_allowance_kg', align: 'right', width: 100, render: (value) => Number(value).toLocaleString() },
        { title: 'Batch KG', dataIndex: 'allowance_kg', align: 'right', width: 100, render: (value) => Number(value).toLocaleString() },
        { title: 'Price', dataIndex: 'unit_price', align: 'right', width: 100, render: money },
        { title: 'Amount', dataIndex: 'line_total', align: 'right', width: 120, render: money },
        { title: 'Override reason', dataIndex: 'override_reason', render: (value) => value || <Text type="secondary">—</Text> },
    ];

    const historyColumns = [
        { title: 'Row', dataIndex: 'row_number', width: 60 },
        { title: 'Result', dataIndex: 'status', width: 110, render: (value) => <Tag color={value === 'CORRECTED' ? 'success' : 'default'}>{value}</Tag> },
        { title: 'Original Employee Name', dataIndex: 'employee_name' },
        { title: 'Original KG', dataIndex: 'kg_input', width: 100 },
        { title: 'Original Rice Item', dataIndex: 'rice_item_input' },
        { title: 'Original Price', dataIndex: 'price_input', width: 110 },
        { title: 'Employee', width: 200, render: (_, row) => (row.employee ? `${row.employee.name} [${row.employee.code}]` : '—') },
        { title: 'Note', dataIndex: 'resolution_note', render: (value) => value || '—' },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Back to drafts</Button>

            <Descriptions size="small" bordered column={{ xs: 1, md: 3 }}>
                <Descriptions.Item label="Batch"><Text strong>{batch.batch_number}</Text></Descriptions.Item>
                <Descriptions.Item label="Status"><Tag color={statusColor[batch.status]}>{batch.status}</Tag></Descriptions.Item>
                <Descriptions.Item label="Month">{dayjs(batch.allowance_month).format('MMMM YYYY')}</Descriptions.Item>
                <Descriptions.Item label="Branch">{batch.branch?.name}</Descriptions.Item>
                <Descriptions.Item label="Warehouse">{batch.location?.name}</Descriptions.Item>
                <Descriptions.Item label="Recipients">{summary.recipients} · {Number(batch.total_quantity).toLocaleString()} kg · {money(batch.total_amount)}</Descriptions.Item>
                {batch.remarks && <Descriptions.Item label="Remarks" span={3}>{batch.remarks}</Descriptions.Item>}
            </Descriptions>

            {isDraft && summary.problems.map((problem) => <Alert key={problem} type="error" showIcon message={problem} />)}
            {isDraft && summary.ready_for_approval && (
                <Alert
                    type="success" showIcon
                    message="Every row is resolved."
                    description="Approve and release this batch with the normal Rice Allowance process: close this window, open the batch in the list, then use Approve and Release Batch. Nothing has been deducted from stock yet."
                />
            )}
            {!isDraft && <Alert type="info" showIcon message={`This batch is ${batch.status}; it can no longer be corrected here.`} />}

            {unresolved.length > 0 && (
                <div>
                    <Text strong>Rows that need correction ({unresolved.length})</Text>
                    <Table
                        style={{ marginTop: 8 }} rowKey="id" size="small" columns={unresolvedColumns} dataSource={unresolved}
                        pagination={{ pageSize: 10, hideOnSinglePage: true }} scroll={{ x: 1120 }}
                        expandable={{
                            expandedRowKeys: unresolved.filter((row) => row.employee_candidates.length > 0).map((row) => row.id),
                            rowExpandable: (row) => row.employee_candidates.length > 0,
                            showExpandColumn: false,
                            expandedRowRender: (row) => (
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                    <Text strong>{row.employee_name} matches more than one employee. Pick the correct record:</Text>
                                    <Radio.Group value={picked[row.id]} disabled={!isDraft} onChange={(event) => setPicked((current) => ({ ...current, [row.id]: event.target.value }))}>
                                        <Space direction="vertical">
                                            {row.employee_candidates.map((candidate) => (
                                                <Radio key={candidate.id} value={candidate.id}>
                                                    <Text strong>{candidate.code}</Text> · {candidate.name} · {candidate.branch || 'No branch'} · {candidate.is_active ? 'Active' : 'Inactive'} · default {Number(candidate.default_kg).toLocaleString()} kg
                                                </Radio>
                                            ))}
                                        </Space>
                                    </Radio.Group>
                                    {isDraft && (
                                        <Button type="primary" icon={<CheckOutlined />} disabled={!picked[row.id]} loading={confirmingRow === row.id} onClick={() => confirmEmployee(row)}>
                                            Same Employee
                                        </Button>
                                    )}
                                </Space>
                            ),
                        }}
                    />
                </div>
            )}

            <div>
                <Text strong>Recipients in this draft ({summary.recipients})</Text>
                <Table
                    style={{ marginTop: 8 }} rowKey="id" size="small" columns={recipientColumns} dataSource={data.recipients}
                    pagination={{ pageSize: 25, hideOnSinglePage: true }} scroll={{ x: 1000 }}
                />
            </div>

            {history.length > 0 && (
                <Collapse
                    size="small"
                    items={[{ key: 'history', label: `Corrected or dismissed rows (${history.length})`, children: <Table rowKey="id" size="small" columns={historyColumns} dataSource={history} pagination={false} scroll={{ x: 800 }} /> }]}
                />
            )}

            <Modal
                title={correcting ? `Correct Excel row ${correcting.row.row_number}` : ''} open={!!correcting}
                onCancel={() => !saving && setCorrecting(null)} onOk={() => correctForm.submit()}
                okText="Add to draft" confirmLoading={saving} width={640} destroyOnHidden
            >
                {correcting && (
                    <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
                        <Text type="secondary">As uploaded: {correcting.row.employee_name || '—'} · {correcting.row.kg_input || '—'} kg · {correcting.row.rice_item_input || '—'} · {correcting.row.price_input || '—'}</Text>
                        {correcting.row.issues.filter((issue) => issue.level === 'error').map((issue) => <Text key={issue.code + issue.message} type="danger">{issue.message}</Text>)}
                    </Space>
                )}
                <Form form={correctForm} layout="vertical" onFinish={submitCorrection}>
                    <Form.Item name="employee_id" label="Employee" rules={[{ required: true, message: 'Choose the employee' }]}
                        extra={correcting?.suggestions?.length ? `Suggested: ${correcting.suggestions.map((s) => `${s.name} [${s.code}]`).join('; ')}` : undefined}>
                        <Select showSearch optionFilterProp="label" options={employeeOptions} placeholder="Choose an existing employee" />
                    </Form.Item>
                    <Form.Item name="item_id" label="Rice Item" rules={[{ required: true, message: 'Choose an existing Rice item' }]}>
                        <Select showSearch optionFilterProp="label" options={itemOptions} placeholder="Choose an existing Rice item" />
                    </Form.Item>
                    <Space size={16} align="start" wrap>
                        <Form.Item name="allowance_kg" label="Batch KG" rules={[{ required: true, message: 'Enter the KG' }]}>
                            <InputNumber min={0.01} max={1000} precision={2} style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item name="unit_price" label="Price" rules={[{ required: true, message: 'Enter the price' }]}>
                            <InputNumber min={0.01} max={999999999} precision={2} prefix="₱" style={{ width: 180 }} />
                        </Form.Item>
                    </Space>
                    <Form.Item name="batch_label" label="Batch label"><Input maxLength={255} /></Form.Item>
                </Form>
            </Modal>

            <Modal
                title={dismissing ? `Dismiss Excel row ${dismissing.row_number}` : ''} open={!!dismissing}
                onCancel={() => !saving && setDismissing(null)} onOk={() => dismissForm.submit()}
                okText="Dismiss row" okButtonProps={{ danger: true }} confirmLoading={saving} destroyOnHidden
            >
                <Text type="secondary">The row stays on record with your reason, but it will not be part of this batch.</Text>
                <Form form={dismissForm} layout="vertical" onFinish={submitDismissal} style={{ marginTop: 12 }}>
                    <Form.Item name="reason" label="Reason" rules={[{ required: true, min: 3, message: 'Enter a reason (at least 3 characters)' }]}>
                        <Input.TextArea rows={3} maxLength={500} />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default RiceAllowanceUploadReview;
